/**
 * The stylized planet surface: one material for every tile. Each vertex carries its tile's random
 * value, its kind, how far it is from the tile's middle (past 2: how far down its wall) and how
 * much of that part of the rim is shore (`aTile`), which type of extractor draws from it (`aGlow`:
 * its glow number, 0 for none), and the layers under the tile (`aColumn`, planet/column.ts). The
 * shader turns them into variation from tile to tile and across the planet, painted detail by
 * kind, moving water with foam where it meets land, a slow pulse in the extractor type's colour
 * (core/palette.ts) on ground being drawn from, and the layers as bands on the walls of pits.
 */
import * as THREE from 'three';

import { MACHINE_COLOR, TILE_TOP } from '@core/palette';

import { GLOW_TYPES } from '../models/machines';
import { LIGHT, NOISE } from './glsl';
import { styleUniforms } from './uniforms';

/**
 * The glow colours, numbered from 1 as `aGlow` counts them (models/extractors.ts). Taken as they
 * are, not converted from sRGB: the shader adds them to the lit colour as light.
 */
const GLOWS = GLOW_TYPES.map((type) =>
  new THREE.Color().setHex(MACHINE_COLOR[type]?.glow ?? 0xffffff, THREE.LinearSRGBColorSpace),
);

/** Every kind's colour by its code, for the layers on a wall; kinds past the palette are grey. */
const LAYER_KINDS = 32;
const LAYERS = Array.from(
  { length: LAYER_KINDS },
  (_, kind) => new THREE.Color(TILE_TOP[kind] ?? 0x808080),
);

const vertexShader = /* glsl */ `
attribute vec4 aTile;
attribute float aGlow;
attribute vec4 aColumn;
flat varying vec4 vColumn;
varying vec3 vNormal;
varying vec3 vView;
varying vec3 vColor;
varying vec3 vObj;
varying vec4 vTile;
varying float vGlow;
void main() {
  vObj = position;
  vColor = color;
  vTile = aTile;
  vGlow = aGlow;
  vColumn = aColumn;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vNormal = normalize(normalMatrix * normal);
  vView = -mv.xyz;
  gl_Position = projectionMatrix * mv;
}
`;

const fragmentShader = /* glsl */ `
${NOISE}
${LIGHT}
uniform float uTime;
uniform float uWaterMotion;
varying vec3 vNormal;
varying vec3 vView;
varying vec3 vColor;
varying vec3 vObj;
varying vec4 vTile;
varying float vGlow;
flat varying vec4 vColumn;

/* Ground an extractor draws from glows in its type's colour, pulsing slowly, strongest towards the
   top's rim: uGlows[aGlow - 1]. */
uniform vec3 uGlows[${String(Math.max(1, GLOWS.length))}];
/* Every kind's colour by its code: the layers on a wall. */
uniform vec3 uLayers[${String(LAYER_KINDS)}];

/* Painted detail by kind (codes from the rules): mottled grass that drifts from yellow to blue
   green, soil crumbs, slabs of stone with cracks, speckled ore; and the deep layers (rules 19):
   basalt in dark columns, gold in bright flecks, the core cracked and glowing from within. */
vec3 groundDetail(int kind, vec3 p, vec3 albedo) {
#ifdef SURFACE_DETAIL
  if (kind == 15) {
    float columns = smoothstep(0.02, 0.07, abs(noise3(p * 6.5) - 0.5));
    return albedo * (0.72 + 0.22 * columns + 0.1 * noise3(p * 21.0));
  }
  if (kind == 16) {
    float fleck = step(0.7, noise3(p * 23.0));
    return albedo * (0.78 + 0.2 * noise3(p * 7.0)) + vec3(0.5, 0.42, 0.18) * fleck;
  }
  if (kind == 17) {
    float crack = smoothstep(0.01, 0.06, abs(noise3(p * 5.0) - 0.5));
    float glow = 0.6 + 0.4 * sin(uTime * 1.3 + noise3(p * 3.0) * 6.2832);
    return albedo * (0.5 + 0.5 * crack) + vec3(0.9, 0.35, 0.08) * (1.0 - crack) * glow;
  }
  if (kind == 2 || kind == 4 || kind == 6) {
    float patches = noise3(p * 2.6);
    vec3 tint = mix(vec3(1.06, 1.02, 0.86), vec3(0.9, 1.0, 1.06), noise3(p * 0.9 + 7.0));
    return albedo * tint * (0.82 + 0.28 * patches + 0.14 * noise3(p * 13.0));
  }
  if (kind == 1) {
    return albedo * (0.8 + 0.24 * noise3(p * 5.0) + 0.2 * step(0.64, noise3(p * 19.0)));
  }
  if (kind == 0) {
    float crack = smoothstep(0.015, 0.05, abs(noise3(p * 4.2) - 0.5));
    return albedo * (0.8 + 0.16 * crack + 0.1 * noise3(p * 17.0));
  }
  if (kind == 11) {
    return albedo * (0.74 + 0.36 * noise3(p * 8.0));
  }
#endif
  return albedo;
}

/* Rock a charge broke (rules 24): dark cracks across its top between lighter chunks. */
vec3 brokenDetail(vec3 p, vec3 albedo) {
#ifdef SURFACE_DETAIL
  float wide = smoothstep(0.0, 0.04, abs(noise3(p * 5.5) - 0.5));
  float fine = smoothstep(0.0, 0.025, abs(noise3(p * 12.0 + 3.7) - 0.5));
  return albedo * (0.4 + 0.6 * wide * (0.6 + 0.4 * fine)) * (0.9 + 0.25 * noise3(p * 21.0));
#else
  return albedo;
#endif
}

/* The wall of a pit, depth height levels below its tile's top: the layers under the tile as
   bands, one level each, their kinds unpacked from the column (four to a float, five bits each,
   the first band lowest; powers of two, so exact), each in its own colour with its painted
   detail, a dark seam between bands, and darker the deeper. */
vec3 wallAlbedo(float depth, vec3 p, float seed) {
  float band = min(floor(depth), 15.0);
  float f = band < 4.0 ? vColumn.x : band < 8.0 ? vColumn.y : band < 12.0 ? vColumn.z : vColumn.w;
  float digit = mod(band, 4.0);
  float shift = digit < 0.5 ? 1.0 : digit < 1.5 ? 32.0 : digit < 2.5 ? 1024.0 : 32768.0;
  float q = floor(f / shift + 0.5 / shift);
  int kind = int(q - floor(q / 32.0) * 32.0 + 0.5);
  vec3 albedo = uLayers[kind] * (0.9 + 0.2 * noise3(p * 0.45)) * (0.94 + 0.12 * seed);
  float within = fract(depth);
  float seam = smoothstep(0.0, 0.12, within) * (1.0 - smoothstep(0.88, 1.0, within));
  albedo *= (0.55 + 0.45 * seam) * (1.0 - 0.025 * min(depth, 14.0));
  return groundDetail(kind, p, albedo);
}

void main() {
  float seed = vTile.x;
  int kind = int(vTile.y + 0.5);
  /* rock a charge broke comes 32 past its kind (planet/tiles.ts): cracked */
  bool broken = kind >= 32;
  if (broken) kind -= 32;
  /* 0 in a top's middle, 1 at its rim, 2 at the foot of the rounded edge, and on down the wall:
     how many height levels below the top */
  float edge = vTile.z;
  float shore = vTile.w;
  vec3 p = vObj;
  vec3 n = normalize(vNormal);
  vec3 v = normalize(vView);
  vec3 albedo = vColor;
  /* broad patches across the planet */
  albedo *= 0.9 + 0.2 * noise3(p * 0.45);
  vec3 color;
  if (edge > 2.001 && vColumn.x >= 0.0) {
    /* a wall with known layers under it: their bands */
    color = stylizedLight(wallAlbedo(edge - 2.0, p, seed), n, v, 0.35);
  } else if (kind == 3) {
    /* water, one surface over a whole lake: shallow and light near land, deep inside; ripples
       bend the light, the sky shows at a glancing angle, the sun glints, foam where it meets land */
    float t = uTime * uWaterMotion;
    albedo = mix(albedo * vec3(0.6, 0.72, 0.9), albedo * 1.08, smoothstep(0.1, 0.9, shore));
    vec3 side = normalize(cross(n, abs(n.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0)));
    vec3 front = cross(n, side);
    float a = noise3(p * 2.4 + vec3(t * 0.21, t * 0.13, -t * 0.17)) - 0.5;
    float b = noise3(p * 5.3 - vec3(t * 0.31, -t * 0.19, t * 0.23)) - 0.5;
    n = normalize(n + (side * a + front * b) * 0.45);
    float fresnel = pow(1.0 - clamp(dot(n, v), 0.0, 1.0), 3.0);
    albedo = mix(albedo, uSkyColor * 1.3, fresnel * 0.5);
    float glint = pow(clamp(dot(reflect(-uSunDir, n), v), 0.0, 1.0), 64.0);
    float churn = noise3(p * 13.0 + vec3(0.0, t * 0.7, t * 0.5));
    float band = smoothstep(0.68, 0.96, shore) * step(edge, 1.001);
    float foam = band * smoothstep(0.42, 0.62, churn) * 0.85;
    albedo = mix(albedo, vec3(0.88, 0.92, 0.94), foam);
    color = stylizedLight(albedo, n, v, 0.35) + uSunColor * glint * (1.0 - foam);
  } else {
    /* every tile a little different; lighter in the middle of its top, darker towards the seams */
    float e = min(edge, 1.0);
    albedo *= (0.92 + 0.16 * seed) * (1.08 - 0.16 * e * e);
    vec3 ground = groundDetail(kind, p, albedo);
    color = stylizedLight(broken ? brokenDetail(p, ground) : ground, n, v, 0.35);
    int glow = int(vGlow + 0.5) - 1;
    if (glow >= 0) {
      float pulse = 0.55 + 0.45 * sin(uTime * 1.7 + seed * 6.2832);
      color += uGlows[glow] * pulse * (0.05 + 0.38 * smoothstep(0.35, 1.0, e));
    }
  }
  gl_FragColor = vec4(color, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/** `detail`: the painted detail by kind (off on the low quality tier). */
export function surfaceMaterial(detail: boolean): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uTime: styleUniforms.uTime,
      uWaterMotion: styleUniforms.uWaterMotion,
      uSunDir: styleUniforms.uSunDir,
      uUpDir: styleUniforms.uUpDir,
      uSunColor: styleUniforms.uSunColor,
      uSkyColor: styleUniforms.uSkyColor,
      uGroundColor: styleUniforms.uGroundColor,
      uRimColor: styleUniforms.uRimColor,
      uGlows: { value: GLOWS },
      uLayers: { value: LAYERS },
    },
    defines: detail ? { SURFACE_DETAIL: '' } : {},
    vertexShader,
    fragmentShader,
    vertexColors: true,
  });
}
